import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {DomSanitizer} from '@angular/platform-browser';

import {TranslateService} from '@ngx-translate/core';

import {defer, EMPTY, from, merge, Observable, of, Subject} from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  finalize,
  map,
  share,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';

import isEmpty from 'lodash-es/isEmpty';

import {ToastrService} from 'ngx-toastr';
import {ProfileMode} from '../../../app.constants';
import {Destroyable} from '../../../destroyable.component';
import {FeatureEnum, ReportsFormData} from '../../../models/models.interfaces';
import {CliCorePropertiesService} from '../../../navigation/cli-core-properties.service';
import {Employee} from '../../employees/employees.model';
import {UploadFileComponent} from '../../files/upload-file/upload-file.component';
import {FormValuesWrapper} from '../../form-generator/form-generator.interfaces';
import {Validators as GpValidators} from '../../forms';
import {LocalizationService} from '../../localization/localization.service';
import {ModalService} from '../../modal';
import {DefaultPagination, Pagination} from '../../ui';
import {
  DropdownTreeviewComponent,
  DropdownTreeviewConfig
} from '../../ui/tree-multi-select/dropdown-treeview.component';
import {TreeItem} from '../../ui/tree-multi-select/treeview.component';
import {User, UserService} from '../../user';
import {isMobileDevice} from '../../utils/browser';
import {FileModel, Files, getFileNameAndExtension, Preview} from '../../utils/files';
import {getWorkflowKey, SEND_DOCUMENT, WorkflowV2Service} from '../../workflow/workflow-v2.service';
import {DocumentsOffering, EmployeeDocument, StatusVM, TemplateAvailability} from '../documents.interface';
import {
  documents_per_page,
  DocumentsService,
  NOT_PUBLISHED_STATUS_FILTER,
  PUBLISHED_STATUS_FILTER
} from '../documents.service';
import {AskForSignaturePopupComponent} from '../ask-for-signature-popup/ask-for-signature-popup.component';
import {SignatureStatusEnum, StatusEnum} from '../../files/files.model';
import {RolesService} from '../../authentication/roles/roles.service';

const UPLOADED_DOCUMENT = 'UPLOADED_DOCUMENT';
const EMPLOYEE_DOCUMENT = 'EMPLOYEE_DOCUMENT';
const SIGNED_SIGNATURE_STATUSES = [
  SignatureStatusEnum.CLIENTCALLBACK_PENDING,
  SignatureStatusEnum.CLIENTCALLBACKERROR,
  SignatureStatusEnum.FINISHED
];
const PENDING_SIGNATURE_STATUSES = [
  SignatureStatusEnum.PROCESSING,
  SignatureStatusEnum.PROVIDERERROR,
  SignatureStatusEnum.SIGNATUREPENDING,
  SignatureStatusEnum.DELETED,
  SignatureStatusEnum.REVOKED
];


@Component({
  selector: 'gp-documents-list',
  templateUrl: './documents-list.component.html',
  styleUrls: ['./documents-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentsListComponent extends Destroyable implements OnInit, OnChanges, AfterViewInit {

  public UPLOADED_DOCUMENT = UPLOADED_DOCUMENT;

  @Input()
  public selectedContract: number;

  @Input()
  public readonly: boolean;

  @Input()
  public cliRegId: number;

  @Input()
  public availability: TemplateAvailability = TemplateAvailability.HR_PRO;

  @Input()
  public employee: Employee;

  @Input()
  public hideTitle = false;

  public loading = false;
  public documents: EmployeeDocument[];
  public pagination: Pagination = new DefaultPagination(documents_per_page);
  public trigger$: Observable<number>;
  public formValuesWrapper: ReportsFormData;
  public confirmModalOpened = false;

  public selectedStatuses: Set<string> = new Set<string>();
  public selectedSignatureStatuses: Set<string> = new Set<string>();

  public filterForm: UntypedFormGroup;

  public statusList: StatusVM[] = [{
    id: 1,
    select: NOT_PUBLISHED_STATUS_FILTER,
    name: 'DOCUMENTS.not_published'
  }, {
    id: 2,
    select: PUBLISHED_STATUS_FILTER,
    name: 'DOCUMENTS.published'
  }];

  public signatureStatusList: StatusVM[] = [{
    id: 1,
    select: SignatureStatusEnum.AVAILABLE,
    name: 'DOCUMENTS.signature_status.READY'
  }, {
    id: 2,
    select: PENDING_SIGNATURE_STATUSES.join(','),
    name: 'DOCUMENTS.signature_status.PENDING'
  }, {
    id: 3,
    select: SIGNED_SIGNATURE_STATUSES.join(','),
    name: 'DOCUMENTS.signature_status.SIGNED'
  }];

  public treeConfig: DropdownTreeviewConfig = {
    isShowFilter: false,
    isShowCollapseExpand: false,
  };

  public statusTree: TreeItem[] = [];
  public saving: boolean;
  public sendLoading: boolean;
  public documentTitleFormCtrl: UntypedFormControl = new UntypedFormControl('', [Validators.required]);

  public sendDocumentForm: UntypedFormGroup;
  public emailForm: UntypedFormGroup;
  public showViewDocumentPanel = false;
  public selectedDocument: EmployeeDocument;
  @ViewChild('uploadFileComponent')
  public uploadFileComponent: UploadFileComponent;
  @ViewChild('statusFilterComponent')
  public statusFilterComponent: DropdownTreeviewComponent;
  @ViewChild('signatureStatusFilterComponent')
  public signatureStatusFilterComponent: DropdownTreeviewComponent;
    public signatureStatusTree: TreeItem[] = [];
  @ViewChild(AskForSignaturePopupComponent)
  public askForSignaturePopupComponent: AskForSignaturePopupComponent;
  public isDocumentCreationAllowed = false;

  public SignatureStatusEnum = SignatureStatusEnum;
  public signatureEnabled = false;
  public hasSignatureRole = false;

  public emailMvelAliases: string[];
  public isMobile = false;
  private uploadedDocument: File;
  private connectedUser: User;
  public currentFileName: string;
  public noSupportedFormat: boolean;
  public reportDefinitionPreview: Preview;
  public previewError: boolean;
  public loadingPreview: boolean;
  public documentsOffering: DocumentsOffering;
  private readonly reloadDocuments$ = new Subject<void>();

  public get isESS() {
    return this.availability === TemplateAvailability.EMPLOYEE;
  }

  constructor(public documentsService: DocumentsService,
              private translate: TranslateService,
              private localizationService: LocalizationService,
              private modalService: ModalService,
              private cd: ChangeDetectorRef,
              private fb: UntypedFormBuilder,
              private toastrService: ToastrService,
              private workflowV2Service: WorkflowV2Service,
              private cliPropertiesService: CliCorePropertiesService,
              private userService: UserService,
              private rolesService: RolesService,
              private sanitizer: DomSanitizer) {
    super();
    this.isMobile = isMobileDevice();

    this.filterForm = this.fb.group({
      name: [''],
      template: [''],
      creator: [''],
      date: ['']
    });

    this.sendDocumentForm = this.fb.group({
      receiver: [''],
      comment: [''],
      documentId: [''],
      documentName: ['']
    });

    this.emailForm = this.fb.group({
      sendEmail: [false],
      email: [{value: '', disabled: true}, [Validators.required, GpValidators.emailValidator]]
    });

    this.emailForm.get('sendEmail').valueChanges.pipe(
      tap(value => {
        const emailControl = this.emailForm.get('email');
        if (value) {
          emailControl.enable();
          if (!emailControl.value) {
            this.emailMvelAliases = ['personalEmail'];
          }
        } else {
          emailControl.disable();
        }
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    this.reloadDocuments$
      .pipe(
        switchMap(() => this.getDocumentsData()),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public get uploadIsValid() {
    return this.documentTitleFormCtrl.valid && !!this.uploadedDocument;
  }

  public ngOnInit(): void {
    this.hasSignatureRole = this.rolesService.hasRole('SIGNATURE');
    this.setDocumentsOffering();
    this.trigger$ = merge(
      this.localizationService.changeDateFormatEvent,
      this.localizationService.changeLanguageEvent
    ).pipe(map(() => Math.random()), share());

    this.documentsService.refreshDocumentList$
      .pipe(
        tap(() => this.refreshDocuments()),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    this.initStatusTree();
    this.initSignatureStatusTree();
    this.userService.getUser().pipe(
      tap((user: User) => this.connectedUser = user),
      takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  public refreshDocuments(): void {
    this.reloadDocuments$.next();
  }

  private refreshDocumentsFromFirstPage(): void {
    this.pagination.currentPage.number = 0;
    this.refreshDocuments();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedContract && this.selectedContract) {
      this.load(this.selectedContract, this.employee, this.cliRegId, this.readonly);
    }
  }

  public ngAfterViewInit() {
    this.filterForm.valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        tap(() => this.refreshDocumentsFromFirstPage()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public openGenerateDocumentModal(): void {
    this.modalService.openModalWithName('GenerateDocumentModalComponent').then((modalRef: any) => {
      modalRef.instance.contractId = this.selectedContract;
      modalRef.instance.employee = this.employee;
      modalRef.instance.cliRegId = this.cliRegId;
      modalRef.instance.formValuesWrapper = this.formValuesWrapper;
      modalRef.instance.connectedUser = this.connectedUser;
      modalRef.instance.modalTitle = 'DOCUMENTS.new_document';
    });
  }

  public download(documentId: number, event?: Event, contextMenu?: { opened: boolean }): void {
    if (event) {
      event.stopPropagation();
    }
    if (contextMenu) {
      contextMenu.opened = false;
    }
    this.documentsService.getDocument(documentId, this.isESS ? ProfileMode.ESS : ProfileMode.CLASSIC).pipe(
      tap((document) => Files.downloadBlob(document.blob, `${document.fileName}`, false)),
      catchError(() => {
        this.toastrService.error(this.translate.instant('COMMON.something_went_wrong'));
        return EMPTY;
      })
    ).subscribe();
  }

  public deleteDocument(documentId: number, event?: Event, contextMenu?: { opened: boolean }): void {
    if (event) {
      event.stopPropagation();
    }
    if (contextMenu) {
      contextMenu.opened = false;
    }
    this.confirmModalOpened = true;
    from(this.modalService.confirm({
      title: 'DIALOG.delete_document',
      text: 'DIALOG.confirm_delete_document?',
      size: 'md',
      customClass: this.showViewDocumentPanel ? 'margin-right-top' : 'margin-top',
      type: 'danger'
    })).pipe(
      delay(500),
      switchMap(confirm => confirm
        ? this.documentsService.deleteDocument(documentId, this.isESS ? ProfileMode.ESS : ProfileMode.CLASSIC)
          .pipe(
            tap(() => {
              this.showViewDocumentPanel = false;
              this.documentsService.refreshDocumentList$.next(true);
            }))
        : EMPTY),
      switchMap(() => this.translate.get('DOCUMENTS.document_has_been_deleted').pipe(
        tap((message: string) => this.toastrService.success(message, '')))
      ),
      finalize(() => {
        this.confirmModalOpened = false;
        this.cd.markForCheck();
      }),
      takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  public openUploadDocumentModal() {
    this.resetUploadForm();
    this.modalService.openModalWithId('upload-document');
  }

  public openAskForSignatureModal(document: EmployeeDocument, event: Event, contextMenu?: { opened: boolean }) {
    event.stopPropagation();
    if (contextMenu) {
      contextMenu.opened = false;
    }
    this.askForSignaturePopupComponent?.open(document, this.cliRegId);
  }

  public checkSignatureStatus(document: EmployeeDocument, event: Event, contextMenu?: { opened: boolean }): void {
    event.stopPropagation();
    if (contextMenu) {
      contextMenu.opened = false;
    }
    this.documentsService.checkSignature(document.id).pipe(
      tap(() => {
        this.documentsService.refreshDocumentList$.next(true);
        this.cd.detectChanges();
      }),
      catchError(() => {
        this.toastrService.error(this.translate.instant('DOCUMENTS.signature_check_failed'));
        this.cd.detectChanges();
        return EMPTY;
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private initStatusTree(): void {
    const treeRoot = new TreeItem('root');
    this.statusTree = [];
    treeRoot.items = [...this.statusList].map((status: StatusVM) =>
      new TreeItem(status.name, status.id, [`${status.select}`]));

    this.statusTree = treeRoot.items;
  }

  private initSignatureStatusTree(): void {
    const treeRoot = new TreeItem('root');

    treeRoot.items = this.signatureStatusList.map((status: StatusVM) =>
      new TreeItem(status.name, status.id, [status.select]));

    this.signatureStatusTree = treeRoot.items;
  }

  public onStatusSelected(ids: number[]): void {
    if (!this.documents) {
      return;
    }

    const selectedStatuses = new Set(this.statusList
      .filter(status => ids.indexOf(status.id) !== -1)
      .map(status => status.select)
    );
    if (this.haveSameValues(selectedStatuses, this.selectedStatuses)) {
      return;
    }

    this.selectedStatuses = selectedStatuses;
    this.refreshDocumentsFromFirstPage();
  }

  public onSignatureStatusSelected(ids: number[]): void {
    if (!this.documents) {
      return;
    }

    const selectedSignatureStatuses = new Set(this.signatureStatusList
      .filter(status => ids.includes(status.id))
      .flatMap(status => status.select.split(','))
    );
    if (this.haveSameValues(selectedSignatureStatuses, this.selectedSignatureStatuses)) {
      return;
    }

    this.selectedSignatureStatuses = selectedSignatureStatuses;
    this.refreshDocumentsFromFirstPage();
  }

  private haveSameValues(first: Set<string>, second: Set<string>): boolean {
    return first.size === second.size && [...first].every(value => second.has(value));
  }

  public isPublished(document: EmployeeDocument): boolean {
    return document.status === StatusEnum.SENT;
  }

  public getSignatureDisplayStatus(signatureStatus: SignatureStatusEnum): 'READY' | 'PENDING' | 'SIGNED' | null {
    if (signatureStatus === SignatureStatusEnum.AVAILABLE) {
      return 'READY';
    }
    if (PENDING_SIGNATURE_STATUSES.includes(signatureStatus)) {
      return 'PENDING';
    }
    if (SIGNED_SIGNATURE_STATUSES.includes(signatureStatus)) {
      return 'SIGNED';
    }
    return null;
  }

  public onDocumentSelect(document: EmployeeDocument, event: Event) {
    if (!this.readonly) {
      const targetElement = <HTMLElement>event.target;
      const isContextMenuButton = targetElement.closest('button')?.classList?.contains('btn-context-menu');
      this.selectedDocument = {...document};
      if ((!isContextMenuButton || this.isMobile)) {
        this.previewDocument(this.selectedDocument.id).subscribe();
        if (!this.showViewDocumentPanel) {
          this.showViewDocumentPanel = true;
        }
      }
    }
  }

  public viewDocument(document: EmployeeDocument, event: Event, contextMenu: { opened: boolean }) {
    event.stopPropagation();

    contextMenu.opened = false;
    this.selectedDocument = {...document};
    if (!this.showViewDocumentPanel) {
      this.showViewDocumentPanel = true;
      this.previewDocument(this.selectedDocument.id).subscribe();
    }
  }

  public openSendDocumentModal(document: EmployeeDocument, event: Event, contextMenu?: { opened: boolean }) {
    event.stopPropagation();
    if (contextMenu) {
      contextMenu.opened = false;
    }
    this.sendDocumentForm.setValue(
      {receiver: this.employee, comment: '', documentId: document.id, documentName: document.name}
    );
    this.emailForm.reset();
    this.modalService.openModalWithId('send-document');
  }

  public sendDocument(modalRef) {
    this.sendLoading = true;
    const workflowKey = getWorkflowKey(SEND_DOCUMENT, this.cliRegId);
    const request = {
      request: {
        contractId: this.selectedContract,
        cliEmpId: this.employee.id,
        documentId: this.sendDocumentForm.get('documentId').value,
        documentName: this.sendDocumentForm.get('documentName').value,
        recipientEmail: this.emailForm.get('sendEmail').value ? this.emailForm.get('email').value : null,
        comment: this.sendDocumentForm.get('comment').value
      },
      cliRegId: this.cliRegId
    };
    this.workflowV2Service.startProcessInstance(workflowKey, request, true, this.isESS ? ProfileMode.ESS : ProfileMode.CLASSIC).pipe(
      tap(() => {
        this.sendLoading = false;
        modalRef.close();
        this.documentsService.refreshDocumentList$.next(true);
        this.cd.detectChanges();
      }),
      catchError(() => {
        this.sendLoading = false;
        this.toastrService.error(this.translate.instant('DOCUMENTS.document_has_not_been_published'));
        this.cd.detectChanges();
        return EMPTY;
      }),
      switchMap(data => {
        if (data) {
          return this.translate.get('DOCUMENTS.document_has_been_published', {document: request.request.documentName}).pipe(
            tap((message: string) => this.toastrService.success(message, '')));
        }
        return EMPTY;
      })
    ).subscribe();
  }

  public load(selectedContract: number, employee: Employee, cliRegId: number, readonly: boolean): void {
    this.selectedContract = selectedContract;
    this.employee = employee;
    this.cliRegId = cliRegId;
    this.readonly = readonly;

    this.documentsService.getTemplates(this.cliRegId, this.availability)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((response: FormValuesWrapper) => this.formValuesWrapper = response);

    this.refreshDocuments();
  }

  public isFiltering() {
    return !isEmpty(Object.values(this.filterForm.value).filter(value => !!value))
      || this.selectedStatuses.size > 0
      || this.selectedSignatureStatuses.size > 0;
  }

  public trackByDocumentId(index: number, document: EmployeeDocument) {
    return document.id;
  }

  public clearAllFilters(): void {
    if (this.isFiltering()) {
      this.selectedStatuses.clear();
      this.selectedSignatureStatuses.clear();
      this.statusFilterComponent?.resetDropdown();
      this.signatureStatusFilterComponent?.resetDropdown();
      if (Object.values(this.filterForm.value).some(value => !!value)) {
        this.filterForm.setValue({date: '', name: '', template: '', creator: ''});
      } else {
        this.refreshDocumentsFromFirstPage();
      }
    }
  }

  public setFileForUpload(file: File) {
    this.uploadedDocument = file;
    if (!this.documentTitleFormCtrl.value) {
      this.documentTitleFormCtrl.patchValue(file ? getFileNameAndExtension(file.name).fileName : '');
    }
  }

  public saveDocument(modalRef: any) {
    this.saving = true;
    const fileExtension = getFileNameAndExtension(this.uploadedDocument.name).fileExtension;

    const name = `${this.documentTitleFormCtrl.value}.${fileExtension}`;
    const payload = {
      name,
      tags: {
        firstName: this.employee?.firstName,
        lastName: this.employee?.lastName,
        id: this.employee?.id,
        creator: `${this.connectedUser.firstName} ${this.connectedUser.lastName}`
      },
      type: EMPLOYEE_DOCUMENT,
      storageType: 'INTERNAL',
      status: 'INITIATED',
      exportableToNifi: false,
      storedCompressed: false,
      contractId: this.selectedContract,
      employeeId: this.employee?.id,
      family: this.UPLOADED_DOCUMENT,
      subFamily: this.UPLOADED_DOCUMENT
    };

    this.documentsService.uploadDocument(this.uploadedDocument, {...payload}, this.isESS ? ProfileMode.ESS : ProfileMode.CLASSIC).pipe(
      tap(() => {
        modalRef.close();
        this.documentsService.refreshDocumentList$.next(true);
      }),
      catchError(() => {
        this.toastrService.error(this.translate.instant('DOCUMENTS.document_has_not_been_saved'));
        this.resetUploadForm();
        return EMPTY;
      }),
      switchMap(data => {
        if (data) {
          return this.translate.get('DOCUMENTS.document_has_been_saved', {document: name}).pipe(
            tap((message: string) => this.toastrService.success(message, '')));
        }
      }),
      finalize(() => {
        this.saving = false;
        this.cd.detectChanges();
      })
    ).subscribe();
  }

  private getDocumentsData(): Observable<{ data: EmployeeDocument[], total: number }> {
    if (!this.selectedContract) {
      return EMPTY;
    }
    let filters = null;
    if (this.isFiltering()) {
      filters = {
        ...this.filterForm.value,
        statuses: this.selectedStatuses,
        signatureStatuses: this.selectedSignatureStatuses
      };
    }

    // Deferred so a cancelled request's finalize cannot clear the loading state of its replacement
    return defer(() => {
      this.loading = true;
      return this.documentsService.fetchDocuments(this.selectedContract,
        this.pagination.currentPage.number,
        filters,
        this.pagination.perPage,
        this.availability,
        this.isESS ? ProfileMode.ESS : ProfileMode.CLASSIC);
    })
      .pipe(
        debounceTime(600),
        tap(response => {
          this.documents = response.data;
          this.pagination.update(response.total);
          this.isDocumentCreationAllowed = response.employeeDocumentCount < this.documentsOffering.maxDocuments;
        }),
        catchError(() => of({data: [], total: 0})),
        finalize(() => {
          this.loading = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.componentDestroyed$)
      );
  }

  private setDocumentsOffering() {
    this.documentsOffering = this.cliPropertiesService.documentsOfferingBehaviorSubject.getValue();
    this.cliPropertiesService.get()
      .pipe(
        tap((properties) => {
          this.signatureEnabled = properties.features?.includes(FeatureEnum.E_SIGNATURE) ?? false;
          this.cd.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private resetUploadForm() {
    this.documentTitleFormCtrl.reset();
    this.setFileForUpload(null);
    this.uploadFileComponent?.deleteFile();
  }

  private previewDocument(documentId: number) {
    this.loadingPreview = true;
    this.currentFileName = 'placeholder text for skeleton text';
    return this.documentsService.getDocument(documentId, this.isESS ? ProfileMode.ESS : ProfileMode.CLASSIC)
      .pipe(
        tap((fileModel: FileModel) => {
          if (fileModel) {
            this.currentFileName = getFileNameAndExtension(fileModel.fileName).fileName;
            if ((navigator as any).msSaveBlob) {
              (navigator as any).msSaveBlob(fileModel.blob, fileModel.fileName);
            } else {
              this.noSupportedFormat = fileModel.blob?.type !== 'application/pdf';
              this.reportDefinitionPreview = this.getDocumentPreview(URL.createObjectURL(fileModel.blob), fileModel.fileName);
              this.previewError = false;
            }
          } else {
            this.previewError = true;
            this.reportDefinitionPreview = null;
            this.currentFileName = '';
          }
        }),
        catchError(() => {
          this.previewError = true;
          this.reportDefinitionPreview = null;
          this.currentFileName = '';
          return EMPTY;
        }),
        finalize(() => {
          this.loadingPreview = false;
          this.cd.detectChanges();
        })
      );
  }

  private getDocumentPreview(href: string, fileName: string): Preview {
    return {
      iframe: this.sanitizer.bypassSecurityTrustHtml(`
             <iframe width="100%" height="100%" src="${href}"
                     name="${fileName}">
             </iframe>
          `), href: this.sanitizer.bypassSecurityTrustUrl(href), download: fileName
    };
  }

  public onCloseSidePanel() {
    this.showViewDocumentPanel = false;
    this.reportDefinitionPreview = null;
    this.currentFileName = '';
    this.previewError = false;
  }
}
